import CloseIcon from "@mui/icons-material/Close";
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, Typography } from "@mui/material";
import MuiAlert from '@mui/material/Alert';
import IconButton from "@mui/material/IconButton";
import TextField from '@mui/material/TextField';
import { useKeycloak } from "@react-keycloak/web";
import axios from 'axios';
import useAxiosKeycloak from '../hooks/useAxiosKeycloak';
import React, { useEffect, useState } from "react";
import QRCode from "react-qr-code";
import texts from '../constants/components/infoSectionText';
import { buildResendNotificationPayload } from '../functions/resendNotification';
import getSignerActions, { EMPTY_SIGNER_ACTIONS } from '../functions/signerActions';
import Sign from "./Sign";
import "./css/Sign.css";


const InfoSection = ({ title, description, startDate, endDate, author, firmantes, idDocument, fechaPub, section, estado, validation_level }) => {
  const [showQRComponent, setShowQRComponent] = useState(false);
  const [openSign, setOpenSign] = useState(false);
  const [qrText, setQrText] = useState("");
  const [signerActions, setSignerActions] = useState(EMPTY_SIGNER_ACTIONS);
  const [documentStatus, setDocumentStatus] = useState('');
  const { keycloak } = useKeycloak();
  const axiosKeycloak = useAxiosKeycloak(keycloak);
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [openRejectConfirmation, setOpenRejectConfirmation] = useState(false);
  const [typeSign, setTypeSign] = useState("");
  const [loading, setLoading] = useState(false);
  const frontendBaseUrl = process.env.REACT_APP_FRONTEND_URL || window.location.origin;

  const ConfirmRejectDialog = ({ open, onClose, onConfirm }) => {
    return (
      <Dialog open={open} sx={{ borderRadius: "0px !important" }}>
        <DialogTitle sx={{ fontWeight: "bold", fontFamily: "robotoMedium" }}>
          {texts.confirmRejectDialog.aceptar}
          <IconButton
            aria-label="Cerrar"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: "robotoMedium", color: "black" }} variant="body1">
            {texts.confirmRejectDialog.estasSeguro}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <Button sx={{ fontFamily: "robotoMedium", color: "black" }} disabled={loading} onClick={onClose} color="primary">
            {texts.confirmRejectDialog.rechazar}
          </Button>
          <Button sx={{ fontFamily: "robotoMedium", backgroundColor: "#316094", borderRadius: "4px", width: "125px", color: "white" }} disabled={loading} onClick={onConfirm} color="primary" autoFocus>
            {texts.confirmRejectDialog.aceptar}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (idDocument) {
          const [docStatusResponse, actions] = await Promise.all([
            axiosKeycloak.get(
              `${process.env.REACT_APP_BACKEND_URL}${process.env.REACT_APP_DOCUMENT_STATE}?id=${idDocument}`,
              { timeout: 20000 }
            ),
            getSignerActions(axiosKeycloak, idDocument),
          ]);

          setDocumentStatus(docStatusResponse.data);
          setSignerActions(actions);
          setTypeSign(actions.tipoFirma || "");

          if (!actions.tipoFirma && section !== "PUBLICADO") {
            setErrorMessage(texts.errorMessages.errorNoCoincideFirmante);
            setErrorSnackbarOpen(true);
          }
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          setErrorMessage(texts.errorMessages.rechazo, error.message);
        } else if (error.code === 'ECONNABORTED') {
          setErrorMessage(texts.errorMessages.peticionTardo);
          setErrorSnackbarOpen(true);
        } else if (error.response && error.response.data) {
          // Extrae y maneja errCode y errorMsg
          const errorMsg = error.response.data.errorMsg;
          setErrorMessage(`${errorMsg}`);
          setErrorSnackbarOpen(true);
        } else {
          setErrorMessage(texts.errorMessages.networkError);
          setErrorSnackbarOpen(true);
        }
      } finally {
        setLoading(false);
      }
    };

    if (section !== "PUBLICADO") { fetchData(); }
  }, [axiosKeycloak, idDocument, section]);


  const handleTraceSign = () => {
    setLoading(true);
    setTypeSign("ELECTRONICA");
    if (window.innerWidth >= 600) {
      const qrUrl = new URL("/documents-to-sign/", frontendBaseUrl);
      qrUrl.searchParams.set("id", idDocument);
      setQrText(qrUrl.toString());
      setShowQRComponent(true);
      setLoading(false);
    } else {
      setOpenSign(true);
      setLoading(false);
    }
  };

  const handleVoiceSign = () => {
    setTypeSign("VOICE");
    setOpenSign(true);
  };

  const handleDigitalSign = () => {
    setTypeSign("DIGITAL");
    setOpenSign(true);
  };

  const handleNegarFirma = () => {
    setLoading(true);
    setOpenRejectConfirmation(true);
    setLoading(false);
  };

  const formatFecha = (fecha) => {
    const date = new Date(fecha);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-4); // Obtener los últimos dos dígitos del año
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };

  const handleCerrarModal = () => {
    setOpenSign(false);
    setOpenRejectConfirmation(false);
    setTypeSign(signerActions.tipoFirma || "");
  };
  const handleCerrarQR = async () => {
    // Actualiza el estado del documento sin recargar la página
    try {
      const actions = await getSignerActions(axiosKeycloak, idDocument);
      setSignerActions(actions);
      setTypeSign(actions.tipoFirma || "");
    } catch (error) {
      if (axios.isCancel(error)) {
        setErrorMessage(texts.errorMessages.rechazo, error.message);
      } else if (error.code === 'ECONNABORTED') {
        setErrorMessage(texts.errorMessages.peticionTardo);
        setErrorSnackbarOpen(true);
      } else if (error.response && error.response.data) {
        // Extrae y maneja errCode y errorMsg
        const errorMsg = error.response.data.errorMsg;
        setErrorMessage(`${errorMsg}`);
        setErrorSnackbarOpen(true);
      } else {
        setErrorMessage(texts.errorMessages.obtenerEstadoFirma);
        setErrorSnackbarOpen(true);
      }
    } finally {
      setShowQRComponent(false);
    }
  };


  const handleRejectConfirmation = async () => {
    setLoading(true);
    setOpenRejectConfirmation(false);
    try {
      // eslint-disable-next-line no-unused-vars
      const response = await axiosKeycloak.put(
        `${process.env.REACT_APP_BACKEND_URL}${process.env.REACT_APP_REJECT_DOC}`,
        {},
        {
          params: { id: idDocument },
          timeout: 20000,
        }
      );

      window.location.href = '/expired-and-rejected-documents/' + idDocument;
    } catch (error) {
      if (axios.isCancel(error)) {
        setErrorMessage(texts.errorMessages.rechazo, error.message);
      } else if (error.code === 'ECONNABORTED') {
        setErrorMessage(texts.errorMessages.peticionTardo);
        setErrorSnackbarOpen(true);
      } else if (error.response && error.response.data) {
        // Extrae y maneja errCode y errorMsg
        const errorMsg = error.response.data.errorMsg;
        setErrorMessage(`${errorMsg}`);
        setErrorSnackbarOpen(true);
      } else {
        setErrorMessage(texts.errorMessages.rechazarFirma);
        setErrorSnackbarOpen(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setLoading(true);
    try {
      const response = await axiosKeycloak.get(`${process.env.REACT_APP_BACKEND_URL}${process.env.REACT_APP_GET_DOC_ID}`, {
        params: { id: idDocument },
        timeout: 20000,
      });
      if (response.data.url) {
        const { url, docName } = response.data;
        const signedDocName = `${docName}-Firmado`;
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", signedDocName || "document-Firmado");
        document.body.appendChild(link);
        link.click();
        if (document.body.contains(link)) {
          document.body.removeChild(link);
        }
      } else {
        setErrorMessage(texts.errorMessages.noHayUrl);
        setErrorSnackbarOpen(true);
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        setErrorMessage(texts.errorMessages.rechazo, error.message);
      } else if (error.code === 'ECONNABORTED') {
        setErrorMessage(texts.errorMessages.peticionTardo);
        setErrorSnackbarOpen(true);
      } else if (error.response && error.response.data) {
        // Extrae y maneja errCode y errorMsg
        const errorMsg = error.response.data.errorMsg;
        setErrorMessage(`${errorMsg}`);
        setErrorSnackbarOpen(true);
      } else {
        setErrorMessage(texts.errorMessages.obtenerUrl,);
        setErrorSnackbarOpen(true);
      }
    } finally {
      setLoading(false);
    }
  };
  const [openDialogEmail, setOpenDialogEmail] = useState(false);
  const [openDialogResendEmail, setOpenDialogResendEmail] = useState(false);
  const [openDialogConfirmResendEmail, setOpenDialogConfirmResendEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [typeSignDigEl, setTypeSignDigEl] = useState("");
  const [newEmailCuit, setNewEmailCuit] = useState("");
  const handleClickOpenDialogEmail = (email, cuit) => {
    setNewEmail(email);
    setNewEmailCuit(cuit)
    setOpenDialogEmail(true);
  };
  const handleClickOpenDialogResendEmail = (email, cuit, sign) => {
    setNewEmail(email);
    setNewEmailCuit(cuit);
    setTypeSignDigEl(sign)
    setOpenDialogResendEmail(true);
  };
  const handleClickOpenDialogConfirmResendEmail = () => {
    setOpenDialogConfirmResendEmail(true);
  };
  const handleCloseResendEmail = () => {
    setOpenDialogResendEmail(false);
  };
  const handleCloseConfirmResendEmail = () => {
    setOpenDialogConfirmResendEmail(false);
  };
  const handleCloseDialogEmail = () => {
    setOpenDialogEmail(false);
  };
  const handleSave = async () => {
    setLoading(true);
    try {
      // Realiza la solicitud PUT para actualizar el correo electrónico
      // eslint-disable-next-line no-unused-vars
      const response = await axiosKeycloak.put(
        `${process.env.REACT_APP_BACKEND_URL}${process.env.REACT_APP_ACTUALIZAR_EMAIL}`,
        {},
        {
          params: {
            id: idDocument,
            cuitFirmante: newEmailCuit,
            nuevoCorreo: newEmail
          },
          timeout: 20000,
        }
      );
      // Cierra el diálogo después de guardar
      handleCloseDialogEmail();
      window.location.href = `/published-documents?id=${idDocument}`;
    } catch (error) {
      if (axios.isCancel(error)) {
        // Manejo de la cancelación de la solicitud
      } else if (error.code === 'ECONNABORTED') {
        setErrorMessage(texts.errorMessages.peticionTardo);
      } else if (error.response && error.response.data) {
        const errorMsg = error.response.data.errorMsg;
        setErrorMessage(`${errorMsg}`);
      } else {
        console.error('Error al hacer la petición:', error.message);
        setErrorMessage(texts.errorMessages.cambiarEmail);
      }
      setErrorSnackbarOpen(true);
    } finally {
      setLoading(false); // Asegúrate de desactivar el loading en el bloque finally
    }
  };
  const [successMessage, setSuccessMessage] = useState("");
  const resendEmail = async () => {
    setLoading(true);
    setSuccessMessage("");
    try {
      const notificationPayload = buildResendNotificationPayload({
        email: newEmail,
        cuil: newEmailCuit,
        signatureType: typeSignDigEl,
        documentId: idDocument,
        documentName: title,
        organizationCode: process.env.REACT_APP_CODIGO_ORGANISMO,
      });

      // Realiza la solicitud POST para reenviar el correo electrónico
      // eslint-disable-next-line no-unused-vars
      const response = await axiosKeycloak.post(
        `${process.env.REACT_APP_BACKEND_URL}${process.env.REACT_APP_REENVIAR_EMAIL}`,
        notificationPayload,
        {
          timeout: 20000,
        }
      );
      // Cierra el diálogo después de guardar
      setSuccessMessage("✔️ Ya se ha reenviado el email.");
      handleCloseResendEmail();
    } catch (error) {
      if (axios.isCancel(error)) {
        // Manejo de la cancelación de la solicitud
      } else if (error.code === 'ECONNABORTED') {
        setErrorMessage(texts.errorMessages.peticionTardo);
      } else if (error.response && error.response.data) {
        const errorMsg = error.response.data.errorMsg;
        setErrorMessage(`${errorMsg}`);
      } else {
        console.error('Error al hacer la petición:', error.message);
        setErrorMessage(texts.errorMessages.cambiarEmail);
      }
      setErrorSnackbarOpen(true);
    } finally {
      setLoading(false); // Asegúrate de desactivar el loading en el bloque finally
    }
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", width: window.innerWidth > 900 ? "35%" : "100%", marginLeft: window.innerWidth > 900 ? "30px" : "0", marginTop: "10px", }}>
      <Typography variant="h5" sx={{
        fontSize: "20px", fontFamily: "robotoMedium", width: "100%", fontWeight: "bold", whiteSpace: 'normal', /* Permite el ajuste de texto en múltiples líneas */
        overflow: 'hidden', /* Oculta cualquier contenido que se desborde */
        wordWrap: 'break-word'
      }} gutterBottom>
        {title}
      </Typography>
      <Typography
        component="p"
        variant="body1"
        className="visually-hidden"
        lang="es"
        aria-live="polite"
        role="status"
        sx={{
          fontSize: "1rem",
          fontFamily: "robotoMedium",
          width: "100%",
          fontWeight: "bold",
          whiteSpace: "normal",
          overflow: "hidden",
          wordWrap: "break-word"
        }}
        gutterBottom
      >
        {(() => {
          const totalFirmantes = firmantes.length;
          const firmados = firmantes.filter(f => f.estadoFirma === "FIRMADO");
          const pendientes = firmantes.filter(f => f.estadoFirma === "PENDIENTE");
          const rechazados = firmantes.filter(f => f.estadoFirma === "RECHAZADO");

          let texto = `Este documento cuenta con ${totalFirmantes} firmantes en total. `;

          if (firmados.length > 0) {
            const listaFirmados = firmados
              .map(f => `${f.nombre} con CUIL ${f.cuil}`)
              .join(", ");
            texto += `Han completado su firma: ${listaFirmados}. `;
          }

          if (pendientes.length > 0) {
            const listaPendientes = pendientes
              .map(f => `${f.nombre} con CUIL ${f.cuil}`)
              .join(", ");
            texto += `Aún tienen pendiente la firma: ${listaPendientes}. `;
          }

          if (rechazados.length > 0) {
            const listaRechazados = rechazados
              .map(f => `${f.nombre} con CUIL ${f.cuil}`)
              .join(", ");
            texto += `Rechazaron la firma: ${listaRechazados}. `;
          }

          return texto;
        })()}
      </Typography>

      {description && <Typography aria-hidden="true" style={{ fontSize: "14px", marginBottom: 16, wordBreak: "break-word", width: "100%", fontWeight: "bold", fontFamily: "robotoMedium", }}>{description}</Typography>}
      <Typography aria-hidden="true" sx={{ fontFamily: "robotoMedium", fontSize: "14px !important", display: "flex", alignItems: "center" }}><Typography component="span" sx={{ fontWeight: "bold", fontSize: "14px !important" }}>{texts.fechaIn}</Typography> <Box sx={{ width: 5 }} /> {formatFecha(startDate)}</Typography>
      <Typography aria-hidden="true" sx={{ fontFamily: "robotoMedium", fontSize: "14px !important", display: "flex", alignItems: "center" }}><Typography component="span" sx={{ fontWeight: "bold", fontSize: "14px !important" }}>{texts.fechaFin}</Typography><Box sx={{ width: 5 }} />  {formatFecha(endDate)}</Typography>
      <Typography aria-hidden="true" sx={{ fontFamily: "robotoMedium", fontSize: "14px !important", display: "flex", alignItems: "center" }}><Typography component="span" sx={{ fontWeight: "bold", fontSize: "14px !important" }}>{texts.fechaPub}</Typography><Box sx={{ width: 5 }} />  {formatFecha(fechaPub)}</Typography>
      <Typography aria-hidden="true" sx={{ fontFamily: "robotoMedium", fontSize: "14px", display: "flex", alignItems: "center" }}><Typography component="span" sx={{ fontWeight: "bold", fontSize: "14px !important" }}> {texts.publicado}</Typography> <Box sx={{ width: 5 }} /> {author}</Typography>
      <div style={{ marginBottom: 16, overflowX: "auto" }}>
        <Typography aria-hidden="true" variant="h6" sx={{ fontWeight: "bold", fontFamily: "robotoMedium", fontSize: "16px", paddingBottom: "3px" }}>{texts.firmantesTable.title}</Typography>
        <div style={{ maxWidth: "100%", overflowX: "auto" }}>
          <Box>
            {firmantes.map((firmante) => (
              <Box
                key={firmante.email}
                sx={{
                  borderBottom: "1px solid #316094",
                  display: "flex",
                  flexDirection: "column",
                  padding: "0px 0px 9px 0px"
                }}
              >
                {/* Nombre + Apellido y CUIT en la misma línea */}
                <Box
                  sx={{
                    display: "flex",
                    fontFamily: "robotoMedium",
                    fontSize: "14px",
                    padding: "3px 0px"
                  }}
                >
                  <Box aria-hidden="true" sx={{ display: "flex" }}>{firmante.nombre}<Box sx={{ width: 5 }} />{firmante.apellido}</Box>
                </Box>
                {/* Email debajo */}
                <Box aria-hidden="true" sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between", // Para separar email del ícono
                  fontFamily: "robotoMedium",
                  fontSize: "14px",
                  padding: "3px 0px"
                }}>
                  <Box sx={{ flexGrow: 1 }} aria-hidden="true">{firmante.email}</Box>
                  {section === "PUBLICADO" && firmante.estadoFirma !== "FIRMADO" && (estado !== "CERRADO" && estado !== "VENCIDO" && estado !== "RECHAZADO") && (
                    <>
                      <IconButton onClick={() => handleClickOpenDialogEmail(firmante.email, firmante.cuit)} sx={{ padding: "0px 8px !important" }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      {estado !== "PENDIENTE" && firmante.estadoFirma === "PENDIENTE" && (
                        <IconButton onClick={() => handleClickOpenDialogResendEmail(firmante.email, firmante.cuit, firmante.tipoFirma)} sx={{ padding: "0 !important" }}>
                          <EmailIcon fontSize="small" />
                        </IconButton>
                      )}
                      <Dialog open={openDialogEmail} BackdropProps={{
                        sx: {
                          backgroundColor: errorMessage ? "transparent" : "rgba(0, 0, 0, 0.5)",
                        },
                      }} PaperProps={{ sx: { width: '60%', maxWidth: 'none' } }} >
                        <IconButton
                          edge="end"
                          aria-label="Cerrar"
                          onClick={handleCloseDialogEmail}
                          sx={{
                            display: "flex", justifyContent: "flex-end", width: "100%", boxShadow: "none", // Desactiva la sombra
                            "&:hover": {
                              backgroundColor: "transparent", // Evita que cambie de color al pasar el mouse
                            },
                            "&:focus": {
                              outline: "none", // Quita el contorno del enfoque
                            },
                          }}
                        >
                          <CloseIcon sx={{ color: "gray" }} />
                        </IconButton>
                        <DialogTitle sx={{ fontFamily: "robotoMedium" }}>{texts.firmantesTable.editarEmail}</DialogTitle>
                        <DialogContent>
                          <TextField
                            autoFocus
                            margin="dense"
                            label="Email"
                            type="email"
                            fullWidth
                            variant="standard"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                          />
                        </DialogContent>
                        <DialogActions>
                          <Button sx={{ fontFamily: "robotoMedium", color: "#316094" }} onClick={handleCloseDialogEmail}>{texts.buttons.cancel}</Button>
                          <Button sx={{ fontFamily: "robotoMedium", color: "#316094" }} disabled={loading} onClick={handleSave}>
                            {loading ? <CircularProgress aria-busy="true"
                              aria-label="Cargando..." size={24} sx={{ color: "#316094" }} /> : texts.buttons.save}
                          </Button>
                        </DialogActions>
                      </Dialog>
                      <Dialog
                        open={openDialogResendEmail}
                        BackdropProps={{
                          sx: {
                            backgroundColor: errorMessage ? "transparent" : "rgba(0, 0, 0, 0.5)",
                          },
                        }}
                        sx={{ maxWidth: 'none', width: "80%", margin: "auto" }}
                        PaperProps={{ sx: { maxWidth: 'none', width: "100%" } }}

                      >
                        <IconButton
                          edge="end"
                          onClick={handleCloseResendEmail}
                          aria-label="Cerrar"
                          sx={{
                            display: "flex", justifyContent: "flex-end", width: "100%", boxShadow: "none", // Desactiva la sombra
                            "&:hover": {
                              backgroundColor: "transparent", // Evita que cambie de color al pasar el mouse
                            },
                            "&:focus": {
                              outline: "none", // Quita el contorno del enfoque
                            },
                          }}
                        >
                          <CloseIcon sx={{ color: "gray" }} />
                        </IconButton>
                        <DialogTitle sx={{ fontFamily: "robotoMedium", display: 'flex', alignItems: 'center' }}>
                          <EmailIcon sx={{ marginRight: 1 }} />
                          Se enviará un nuevo email al firmante recordándole firmar este documento

                        </DialogTitle>
                        <DialogContent sx={{ marginTop: 2 }}>
                          <Typography variant="body2" sx={{ fontFamily: "robotoMedium", color: "#555" }}>
                            Se le notificará al siguiente email: <strong style={{ color: "#316094" }}>{newEmail}</strong>
                          </Typography>
                        </DialogContent>
                        <DialogActions>
                          <Button sx={{ fontFamily: "robotoMedium", color: "#316094" }} onClick={handleCloseResendEmail}>
                            {texts.buttons.cancel}
                          </Button>
                          <Button sx={{ fontFamily: "robotoMedium", color: "#316094" }} disabled={loading} onClick={handleClickOpenDialogConfirmResendEmail}>
                            {loading ? <CircularProgress aria-busy="true"
                              aria-label="Cargando..." size={24} sx={{ color: "#316094" }} /> : "Enviar"}
                          </Button>
                        </DialogActions>
                      </Dialog>
                      <Dialog
                        open={openDialogConfirmResendEmail}
                        BackdropProps={{
                          sx: {
                            backgroundColor: errorMessage ? "transparent" : "rgba(0, 0, 0, 0.5)",
                          },
                        }}
                        sx={{ width: "100%" }}
                        PaperProps={{ width: "100%" }}
                      >
                        <IconButton
                          edge="end"
                          onClick={handleCloseConfirmResendEmail}
                          aria-label="Cerrar"
                          sx={{
                            display: "flex", justifyContent: "flex-end", width: "100%", boxShadow: "none", // Desactiva la sombra
                            "&:hover": {
                              backgroundColor: "transparent", // Evita que cambie de color al pasar el mouse
                            },
                            "&:focus": {
                              outline: "none", // Quita el contorno del enfoque
                            },
                          }}
                        >
                          <CloseIcon sx={{ color: "gray" }} />
                        </IconButton>
                        <DialogContent >
                          <Typography variant="body2" sx={{ fontFamily: "robotoMedium", fontSize: "18px" }}>
                            {successMessage ? successMessage : "¿Confirma la operación?"}
                          </Typography>
                        </DialogContent>
                        {successMessage ? null : <DialogActions>
                          <Button sx={{ fontFamily: "robotoMedium", color: "#316094" }} onClick={handleCloseConfirmResendEmail}>
                            {texts.buttons.cancel}
                          </Button>
                          <Button sx={{ fontFamily: "robotoMedium", color: "#316094" }} disabled={loading} onClick={resendEmail}>
                            {loading ? <CircularProgress aria-busy="true"
                              aria-label="Cargando..." size={24} sx={{ color: "#316094" }} /> : "Confirmar"}
                          </Button>
                        </DialogActions>}
                      </Dialog>
                    </>
                  )}
                </Box>
                {/* cuit */}
                <Box sx={{ fontFamily: "robotoMedium", fontSize: "14px", padding: "5px 0px", display: "flex" }}>
                  CUIL<Box sx={{ width: 5 }} />{firmante.cuit}
                </Box>
                {/* Tipo de firma */}
                <Box sx={{ fontFamily: "robotoMedium", fontSize: "14px", padding: "3px 0px", display: "flex" }}>
                  Firma<Box sx={{ width: 5 }} />{firmante.tipoFirma}
                </Box>
                {/* Firma y Estado del documento */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start", // Ajuste en pantallas pequeñas
                    fontFamily: "robotoMedium",
                    fontSize: "14px",
                  }}
                >
                  <Box sx={{ padding: "3px 0px", fontWeight: "bold", display: "flex" }}>Estado de la firma:<Box sx={{ width: 5 }} />{firmante.estadoFirma}</Box>
                  <Box sx={{ padding: "3px 0px", fontWeight: "bold", display: "flex" }}>
                    último cambio:<Box sx={{ width: 5 }} />{formatFecha(firmante.fechaEstadoFirma)}
                  </Box>
                </Box>


              </Box>
            ))}
          </Box>

        </div>
      </div>
      {!loading ? (
        documentStatus === "PUBLICADO" && (signerActions.puedeFirmar || signerActions.puedeRechazar) ? (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingTop: '20px'
            }}
          >
            {signerActions.puedeRechazar && (
              <Button
                onClick={handleNegarFirma}
                disabled={loading}
                style={{
                  color: "#000000",
                  fontFamily: "robotoRegular",
                  textDecoration: "none",
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                }}
              >
                {texts.buttons.denegar}
              </Button>
            )}

            {signerActions.puedeFirmar && signerActions.tipoFirma === "ELECTRONICA" ? (
              <Box className="info-section-buttons-container">
                <span id="signing-options-label" className="sr-only" lang="es">Elegí el mecanismo de firma electrónica biométrica a utilizar.</span>

                <Button
                  variant="contained"
                  onClick={handleTraceSign}
                  disabled={loading}
                  aria-describedby="signing-options-label"
                  className="info-section-signing-button"
                  style={{ backgroundColor: "#316094" }}
                >
                  Firmar por Trazo
                </Button>

                <Button
                  variant="contained"
                  onClick={handleVoiceSign}
                  disabled={loading}
                  aria-describedby="signing-options-label"
                  className="info-section-signing-button"
                  style={{ backgroundColor: "#316094" }}
                >
                  Firmar con la Voz
                </Button>
              </Box>
            ) : signerActions.puedeFirmar && signerActions.tipoFirma === "DIGITAL" ? (
              <Button
                variant="contained"
                onClick={handleDigitalSign}
                disabled={loading}
                className="info-section-signing-button"
                style={{ backgroundColor: "#316094" }}
              >
                Firmar digitalmente
              </Button>
            ) : null}
          </div>
        ) : documentStatus === "RECHAZADO" ? (
          <div style={{ fontFamily: "robotoMedium", color: "red" }}>
            {texts.documentStatus.rechazo}
          </div>
        ) : documentStatus === "VENCIDO" ? (
          <div style={{ textAlign: "center", fontFamily: "robotoMedium", color: "red" }}>
            {texts.documentStatus.vencido}
          </div>
        ) : documentStatus === "PUBLICADO" && !signerActions.puedeFirmar ? (
          <div style={{ fontFamily: "robotoMedium", color: "#FFD300" }}>
            {texts.documentStatus.proceso}
          </div>
        ) : documentStatus === "CERRADO" ? (
          <div>
            <div style={{ fontFamily: "robotoMedium", color: "green", marginBottom: "10px" }}>
              {texts.documentStatus.firmado}
            </div>
            <Button
              variant="contained"
              sx={{ bgcolor: "#316094", fontFamily: "robotoMedium", borderRadius: "4px", width: "110px" }}
              onClick={handleDownload}
              disabled={loading}
            >
              {loading ? <CircularProgress aria-busy="true"
                aria-label="Cargando..." sx={{ color: "#316094" }} size={24} /> : texts.buttons.dowload}
            </Button>
          </div>
        ) : section === "PUBLICADO" && estado === "CERRADO" ? (
          <div>
            <Button
              variant="contained"
              sx={{ bgcolor: "#316094", fontFamily: "robotoMedium", borderRadius: "4px", width: "110px" }}
              onClick={handleDownload}
              disabled={loading}
            >
              {loading ? <CircularProgress aria-busy="true"
                aria-label="Cargando..." sx={{ color: "#316094" }} size={24} /> : texts.buttons.dowload}
            </Button>
          </div>
        ) : null
      ) : (
        <CircularProgress aria-busy="true"
          aria-label="Cargando..." sx={{ color: "#316094" }} />
      )}
      <Dialog open={openSign} >
        <div style={{ padding: '35px', justifyContent: 'center', textAlign: 'center' }}>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleCerrarModal}
            aria-label="Cerrar"
            style={{
              position: "absolute",
              top: 1,
              right: 15,
            }}
          >
            <CloseIcon sx={{ color: "#316094" }} />
          </IconButton>
          <Sign idDocument={idDocument} keycloak={keycloak} typeSign={typeSign} validation_level={validation_level} handleClose={handleCerrarModal} />
        </div>
      </Dialog>

      <Dialog open={showQRComponent} sx={{ borderRadius: "0px", }} maxWidth="xs" fullWidth>
        <div style={{ position: "relative", padding: "35px", display: "flex", flexDirection: "column", alignItems: "center", backgroundColor: "#E2E2E5", overflow: "hidden" }}>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleCerrarQR}
            aria-label="Cerrar"
            style={{
              position: "absolute",
              top: 1,
              right: 15,
            }}
          >
            <CloseIcon sx={{ color: "#316094" }} />
          </IconButton>
          <Typography
            component="p"
            className="sr-only"
            lang="es"
            aria-live="assertive"
          >
            La pantalla presenta un código QR para escanear.
            Si no puede hacerlo, cierre esta pantalla y firme por voz.
          </Typography>
          <Typography sx={{ fontFamily: "robotoMedium", color: "#316094", marginBottom: "10px", textAlign: "center" }}>
            {texts.qr}
          </Typography>
          <QRCode style={{ padding: "10px", border: "2px solid #316094", maxWidth: '100%', height: 'auto', marginBottom: 16 }} value={qrText} />
        </div>
      </Dialog>

      <ConfirmRejectDialog
        open={openRejectConfirmation}
        onClose={() => setOpenRejectConfirmation(false)}
        onConfirm={handleRejectConfirmation}
      />

      <Snackbar
        open={errorSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setErrorSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={() => setErrorSnackbarOpen(false)}
          severity="error"
          sx={{ fontFamily: "robotoMedium", }}
        >
          {errorMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

export default InfoSection;
